import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Paper, Popper } from "@material-ui/core";

const GAP = 3;
const ACTION_GAP = 4;

// Include the actual width of the counter for each possible number of hidden badges.
export function visibleBadgeCount(widths, available, counterWidth) {
  const prefixes = [0];
  widths.forEach((width, index) => {
    prefixes.push(prefixes[index] + width + (index ? GAP : 0));
  });
  if (prefixes[widths.length] <= available) return widths.length;
  for (let count = widths.length - 1; count >= 0; count -= 1) {
    if (prefixes[count] + (count ? GAP : 0) + counterWidth(widths.length - count) <= available) return count;
  }
  return 0;
}

export default function TicketBadgeList({ badges, actionsRef, classes, indicatorStyle }) {
  const rowRef = useRef(null);
  const measureRef = useRef(null);
  const counterRef = useRef(null);
  const [layout, setLayout] = useState({ count: 0, width: null });
  const [anchor, setAnchor] = useState(null);
  const [pinned, setPinned] = useState(false);
  // Ticket updates can recreate arrays without changing the badge contents.
  const badgeKey = JSON.stringify(badges);

  useLayoutEffect(() => {
    const row = rowRef.current;
    const measure = measureRef.current;
    if (!row || !measure || !row.parentElement) return undefined;
    let active = true;
    const update = () => {
      if (!active) return;
      const parentRect = row.parentElement.getBoundingClientRect();
      const rowRect = row.getBoundingClientRect();
      if (!parentRect.width) return;
      const actions = actionsRef.current;
      if (!actions) return;
      const actionRect = actions && actions.getBoundingClientRect();
      const right = actionRect && actionRect.width ? Math.min(parentRect.right, actionRect.left - ACTION_GAP) : parentRect.right;
      const available = Math.max(0, right - rowRect.left);
      const widths = Array.from(measure.querySelectorAll("[data-measure-badge]")).map(element => element.getBoundingClientRect().width);
      const counter = measure.querySelector("[data-measure-counter]");
      const count = visibleBadgeCount(widths, available, hidden => {
        counter.textContent = `+${hidden}`;
        return counter.getBoundingClientRect().width;
      });
      setLayout(previous => previous.count === count && previous.width === available ? previous : { count, width: available });
    };
    update();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    if (observer) {
      observer.observe(row.parentElement);
      observer.observe(measure);
    }
    // The action container is a later sibling and its ref may be attached after this effect.
    const frame = requestAnimationFrame(() => {
      if (observer && actionsRef.current) observer.observe(actionsRef.current);
      update();
    });
    window.addEventListener("resize", update);
    if (document.fonts) document.fonts.ready.then(update);
    return () => {
      active = false;
      cancelAnimationFrame(frame);
      if (observer) observer.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [actionsRef, badgeKey]);

  const hidden = badges.slice(layout.count);
  useEffect(() => {
    if (!hidden.length) {
      setAnchor(null);
      setPinned(false);
    }
  }, [hidden.length]);
  useEffect(() => {
    if (!pinned) return undefined;
    const close = event => {
      if (counterRef.current && !counterRef.current.contains(event.target)) {
        setPinned(false);
        setAnchor(null);
      }
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [pinned]);

  return (
    <>
      <div ref={rowRef} className={classes.tags} style={{ maxWidth: layout.width === null ? 0 : layout.width }}>
        {badges.slice(0, layout.count).map(badge => <span key={badge.key} className={classes.tag} style={badge.style}>{badge.name}</span>)}
        {hidden.length > 0 && (
          <button
            ref={counterRef}
            type="button"
            className={`${classes.tag} ${classes.moreTag}`}
            style={indicatorStyle}
            aria-label={`${hidden.length} etiquetas ocultas: ${hidden.map(badge => badge.name).join(", ")}`}
            aria-expanded={Boolean(anchor)}
            onMouseEnter={event => setAnchor(event.currentTarget)}
            onMouseLeave={() => { if (!pinned) setAnchor(null); }}
            onFocus={event => setAnchor(event.currentTarget)}
            onBlur={() => { if (!pinned) setAnchor(null); }}
            onClick={event => {
              event.stopPropagation();
              setPinned(!pinned);
              setAnchor(pinned ? null : event.currentTarget);
            }}
            onKeyDown={event => { if (event.key === "Escape") { setPinned(false); setAnchor(null); } }}
          >+{hidden.length}</button>
        )}
      </div>
      <div className={classes.tagMeasure} aria-hidden="true">
        <div ref={measureRef} className={classes.tagMeasureContent}>
          {badges.map(badge => <span key={badge.key} data-measure-badge className={classes.tag} style={badge.style}>{badge.name}</span>)}
          <button type="button" tabIndex={-1} data-measure-counter className={`${classes.tag} ${classes.moreTag}`} style={indicatorStyle}>+1</button>
        </div>
      </div>
      <Popper
        open={Boolean(anchor) && hidden.length > 0}
        anchorEl={anchor}
        placement="bottom-start"
        style={{ pointerEvents: "none", zIndex: 1500 }}
      >
        <Paper className={classes.hiddenTagsPaper}>
          <div className={classes.hiddenTagsList}>
            {hidden.map(badge => <span key={badge.key} className={classes.tag} style={badge.style}>{badge.name}</span>)}
          </div>
        </Paper>
      </Popper>
    </>
  );
}
